import cn from 'classnames';

import { useCheckoutContext, Article, Price } from '@zooplus/checkout-state';
import {
  P2,
  ProductReducedPrice,
  ProductStandardPrice,
  ProductBestPrice,
} from '@zooplus/ui-components';

import { useTranslation } from '@hooks/use-translations';
import { CART_VERSION, CHECKOUT_STATE_PROJECT_ID, PRICE_TYPE } from '@utilities/constants';

import styles from './item-price.module.scss';

type ItemPriceProps = {
  priceType: string;
  article: Article;
  commonStyle?: string;
};
const ItemPrice = ({ priceType, article, commonStyle }: ItemPriceProps) => {
  const { t } = useTranslation();
  const { mainTogglesMap = {}, cartVersion } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;

  const getReducedPriceProps = () => {
    return {
      isOmnibusDirectiveFeatureEnabled: mainTogglesMap.CHECKOUT_OMNIBUS_DIRECTIVE_FEATURE,
      tooltipProps: {
        id: 'product-reduced-price-tooltip',
        label: 'more info',
        text: article.localizedTooltipText,
        width: 'md' as const,
        breakpointPlacements: {
          sm: { position: 'top-middle' as const },
        },
      },
      refPrice: isCartApiV2
        ? {
            amount: article.referencePrice ? (article.referencePrice?.label as string) : '',
            difference: article.percentageDiscount ? `${article.percentageDiscount}%` : '',
            label: article.referencePrice ? (article.referencePrice?.labelPrefix as string) : '',
          }
        : {
            amount: article.formattedBeforePrice as string,
            difference: article.percentageDiscount ? `${article.percentageDiscount}%` : '',
            label: article.beforePriceLabel as string,
          },
      pricePrefix: isCartApiV2
        ? article.referencePrice
          ? `${article.referencePrice?.labelPrefix} ${article.referencePrice?.label}`
          : ''
        : `${article.beforePriceLabel} ${article.formattedBeforePrice}`,
      reducedPriceStyleClass: cn(
        styles.price,
        !article.percentageDiscount && styles.removePercentageSection,
      ),
    };
  };

  switch (priceType) {
    case PRICE_TYPE.REDUCED_PRICE: {
      const reducedPriceProps = getReducedPriceProps();
      return (
        <ProductReducedPrice
          data-testid="article-price"
          className={cn(commonStyle, reducedPriceProps.reducedPriceStyleClass)}
          pricePrepend={
            isCartApiV2
              ? (article?.price as Price)?.labelPrefix || ''
              : article.actualPriceLabel || ''
          }
          price={
            isCartApiV2 ? (article?.price as Price)?.label || '' : article.formattedPrice || ''
          }
          refPrice={
            reducedPriceProps.isOmnibusDirectiveFeatureEnabled && article.percentageDiscount
              ? reducedPriceProps.refPrice
              : undefined
          }
          pricePrefix={reducedPriceProps.pricePrefix}
          tooltip={
            reducedPriceProps.isOmnibusDirectiveFeatureEnabled && article.localizedTooltipText
              ? reducedPriceProps.tooltipProps
              : undefined
          }
        />
      );
    }
    case PRICE_TYPE.BEST_PRICE: {
      const tooltipText = (
        <div className={styles.tooltipTextWrapper}>
          <h3>{isCartApiV2 ? (article?.price as Price)?.labelPrefix : article.actualPriceLabel}</h3>
          <P2>{t('cart.overview.bpa.popover.text')}</P2>
        </div>
      );
      return (
        <ProductBestPrice
          data-testid="best-price"
          className={commonStyle}
          price={`${isCartApiV2 ? (article?.price as Price)?.label || '' : article.formattedActualPrice || ''}`}
          pricePrefix={`${isCartApiV2 ? (article?.price as Price)?.labelPrefix || '' : article.actualPriceLabel || ''}`}
          tooltipId={`${article.id}${article.variantName}`}
          tooltipLabel="More info"
          tooltipText={tooltipText}
          tooltipPosition="top-left"
          tooltipBreakpointPlacements={{
            md: {
              position: 'right-middle',
            },
          }}
        />
      );
    }

    default:
      return (
        <ProductStandardPrice
          data-testid="article-price"
          className={commonStyle}
          price={`${isCartApiV2 ? (article?.price as Price)?.label || '' : article.formattedActualPrice || ''}`}
        />
      );
  }
};

export default ItemPrice;
